import { useId, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * Zoekkiezer (docs/UX-RICHTLIJNEN.md §6, "Zoekkiezer"): typen om te zoeken, kaartjes met naam en details in groepen,
 * onderaan eventueel "+ Nieuwe … «getypte naam»". Vervangt een lange keuzelijst waar je uit het adresboek kiest.
 */
export interface ComboItem {
  value: string;
  label: string;
  /** Tweede regel: vakgebied, telefoon, functie. */
  meta?: string;
  /** Groepskop; items met dezelfde groep staan onder elkaar, in de volgorde waarin ze binnenkomen. */
  group?: string;
  /** Rechts in de rij, bijv. een chip voor de erkenning. */
  badge?: ReactNode;
}

const norm = (s: string) => s.toLocaleLowerCase("nl").normalize("NFD").replace(/[̀-ͯ]/g, "");

export function Combobox({
  id,
  label,
  items,
  value,
  onChange,
  placeholder = "Zoek op naam",
  clearLabel,
  addLabel,
  onAdd,
  hint,
  error,
  disabled,
  className,
}: {
  id: string;
  label: string;
  items: ComboItem[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Eerste keuze die het veld leegmaakt (bijv. "Nog niet bekend"); weglaten als kiezen verplicht is. */
  clearLabel?: string;
  /** Tekst van de laatste rij, met wat er getypt is; alleen met `onAdd`. */
  addLabel?: (query: string) => string;
  onAdd?: (query: string) => void;
  hint?: ReactNode;
  error?: string;
  disabled?: boolean;
  className?: string;
}) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const selected = items.find((i) => i.value === value) ?? null;

  type Row = { kind: "clear" } | { kind: "item"; item: ComboItem } | { kind: "add" };
  const rows = useMemo<Row[]>(() => {
    const q = norm(query.trim());
    const hits = q ? items.filter((i) => norm(`${i.label} ${i.meta ?? ""}`).includes(q)) : items;
    return [
      ...(clearLabel && !q && value ? [{ kind: "clear" as const }] : []),
      ...hits.map((item) => ({ kind: "item" as const, item })),
      ...(onAdd ? [{ kind: "add" as const }] : []),
    ];
  }, [items, query, clearLabel, value, onAdd]);

  function show() {
    if (disabled) return;
    setQuery("");
    setActive(
      Math.max(
        0,
        rows.findIndex((r) => r.kind === "item" && r.item.value === value),
      ),
    );
    setOpen(true);
  }
  function choose(r: Row) {
    setOpen(false);
    setQuery("");
    if (r.kind === "clear") onChange(null);
    else if (r.kind === "item") onChange(r.item.value);
    else onAdd?.(query.trim());
  }

  const fieldClass = className ? `field combo ${className}` : "field combo";
  let lastGroup: string | undefined;
  return (
    <div className={fieldClass}>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        ref={input}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && rows[active] ? `${uid}-o${active}` : undefined}
        aria-invalid={error ? true : undefined}
        autoComplete="off"
        disabled={disabled}
        value={open ? query : (selected?.label ?? "")}
        placeholder={open && selected ? selected.label : placeholder}
        onFocus={(e) => e.target.select()}
        onClick={() => !open && show()}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(!open && selected && v.startsWith(selected.label) ? v.slice(selected.label.length) : v);
          setActive(0);
          setOpen(true);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) show();
            else setActive((a) => Math.min(rows.length - 1, a + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
          } else if (e.key === "Enter" && open) {
            e.preventDefault();
            const r = rows[active];
            if (r) choose(r);
          } else if (e.key === "Escape" && open) {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
            setQuery("");
          }
        }}
      />
      {!open && selected?.meta && <span className="hint">{selected.meta}</span>}
      {open && (
        <ul className="combolist" role="listbox" id={listId} aria-label={label.replace(/\s*\*$/, "")}>
          {rows.length === 0 && <li className="combonone">Niets gevonden</li>}
          {rows.map((r, k) => {
            const head =
              r.kind === "item" && r.item.group && r.item.group !== lastGroup ? (
                <li className="combogroup" role="presentation" key={`g-${r.item.group}`}>
                  {r.item.group}
                </li>
              ) : null;
            if (r.kind === "item") lastGroup = r.item.group;
            const common = {
              id: `${uid}-o${k}`,
              role: "option" as const,
              "aria-selected": k === active,
              onMouseDown: (e: { preventDefault: () => void }) => e.preventDefault(),
              onMouseEnter: () => setActive(k),
              onClick: () => choose(r),
            };
            if (r.kind === "clear")
              return (
                <li key="clear" {...common} className="comboclear">
                  {clearLabel}
                </li>
              );
            if (r.kind === "add")
              return (
                <li key="add" {...common} className="comboadd">
                  <span aria-hidden="true">+</span> {addLabel ? addLabel(query.trim()) : "Nieuw toevoegen"}
                </li>
              );
            const it = r.item;
            return [
              head,
              <li key={it.value} {...common} data-current={it.value === value || undefined}>
                <span className="combotext">
                  <span className="combolabel">{it.label}</span>
                  {it.meta && <span className="small muted">{it.meta}</span>}
                </span>
                {it.badge}
              </li>,
            ];
          })}
        </ul>
      )}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="hint err">{error}</span>}
    </div>
  );
}
